import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { gamingDayOf, gamingDayOffset, type PlayerRow } from '@dayon/contracts';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { downloadRun } from '../../reports/download.ts';
import type { ExportOutcome } from '../types.ts';

/**
 * Export the roster.
 *
 * It runs through the REPORTS MODULE — `POST /api/players/export` hands a named
 * spec to the same engine the regulatory extracts and the audit trail use — so
 * it inherits the closed-map builder, the 5,000-row cap, the statement timeout,
 * the CSV formula guard, the read-only database role and the stored artefact
 * that makes the same file downloadable again next week. A second export path
 * would be a second place for every one of those to be forgotten.
 *
 * WHAT THE FORM DOES NOT OFFER, AND WHY. The design's dialog has Scope, Format
 * and Delivery selects. Format is CSV because the engine writes CSV and nothing
 * in this platform renders XLSX or PDF. Delivery is a download because
 * nothing scheduled produces this file: `@dayon/mailer` sends a REPORT
 * DELIVERY on a `reporting.report_schedule`, and a roster export is a run
 * somebody asked for, on this screen, with the window they just chose. Mailing
 * it would be a second delivery path with no schedule behind it, and there is
 * no SFTP client anywhere in the repository. Scope is the brand
 * scope and the registration window and nothing else: the report engine filters
 * on a date range and a brand, deliberately — every other predicate would have
 * to become SQL assembled from a request, which is the exact thing that file
 * exists to prevent. The roster's status, tier, KYC and agent filters are
 * therefore COLUMNS in the file rather than filters on it, so the reader
 * narrows in the spreadsheet.
 */
export function ExportDialog({
  open,
  onClose,
  brandId,
  brandLabel,
  filtersApplied,
}: {
  open: boolean;
  onClose: () => void;
  brandId: string | null;
  brandLabel: string;
  /** Which roster narrowings are set, named so the notice can be specific. */
  filtersApplied: readonly string[];
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [from, setFrom] = useState(() => gamingDayOffset(-365 * 3));
  const [to, setTo] = useState(() => gamingDayOffset(0));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ExportOutcome | null>(null);
  /** The oldest registration in scope, once the probe lands. Kept from one opening to the next. */
  const [earliest, setEarliest] = useState<string | null>(null);
  /* The same day, for the effect below that runs as the dialog opens: it reads the day as it
     stands then, and must not run again — resetting the dates under the reader — each time a
     read changes it. Written wherever `earliest` is. */
  const earliestRead = useRef<string | null>(null);
  /**
   * Where the read of the oldest registration stands: in flight, read (the day is `earliest`), read
   * and nobody is registered, or failed. Until it answers, the window is not known to cover
   * everybody, so Run waits and the hint says the dialog is reading.
   */
  const [oldest, setOldest] = useState<'reading' | 'read' | 'none' | 'failed'>('reading');

  /*
   * THE DATES ARE READ AGAIN EACH TIME THE DIALOG OPENS, not once when the roster mounted: a
   * Players screen loaded at 23:50 and exported at 00:10 Manila time otherwise offered yesterday
   * as "Registered to" — under a hint that says the whole roster is covered. Today's gaming day;
   * and From the oldest registration already read, kept while the read below runs again, so a
   * reopening does not flash three years ago. The three-year fallback is for a first opening.
   */
  useEffect(() => {
    if (!open) return;
    setFrom(earliestRead.current ?? gamingDayOffset(-365 * 3));
    setTo(gamingDayOffset(0));
  }, [open]);

  /*
   * The default window is not a guess.
   *
   * A range that started three years ago would silently drop every account
   * older than that, and a roster with people missing from it is worse than no
   * roster. So the dialog asks the roster itself for the OLDEST registration in
   * scope — sort=REGISTERED with direction=ASC, one row — and starts the window
   * there. That read is also the plainest possible proof that ascending order
   * now works: before this, the route could only answer "newest first".
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOldest('reading');
    const params = new URLSearchParams({
      perPage: '1',
      sort: 'REGISTERED',
      direction: 'ASC',
    });
    if (brandId) params.set('brandId', brandId);
    api
      .get<{ items: PlayerRow[] }>(`/api/players?${params}`)
      .then((d) => {
        const first = d.items[0];
        if (cancelled) return;
        if (!first) {
          /* Nobody is registered in scope, so there is no oldest registration to start from. */
          earliestRead.current = null;
          setEarliest(null);
          setFrom(gamingDayOffset(-365 * 3));
          setOldest('none');
          return;
        }
        // registeredAt is not on the list row; lastSeenAt is. The row we want is
        // identified by the order, and the date comes from the record read.
        return api
          .get<{ player: { registered_at: string } }>(`/api/players/${first.id}`)
          .then((record) => {
            if (cancelled) return;
            /* The gaming day it fell in, which is what the window counts and what the file's
               Registered column prints: its UTC date is a day early for anybody who joined
               before 08:00 Manila time. */
            const day = gamingDayOf(record.player.registered_at);
            earliestRead.current = day;
            setEarliest(day);
            setFrom(day);
            setOldest('read');
          });
      })
      .catch(() => {
        /* A failed probe falls back to the three-year default and says so below. It
           is not worth a modal, and Run is not held back: the operator can widen the
           window by hand, and the dialog states that the default is a fallback rather
           than the roster's real floor. */
        if (cancelled) return;
        earliestRead.current = null;
        setEarliest(null);
        setFrom(gamingDayOffset(-365 * 3));
        setOldest('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [open, brandId]);

  const rangeError = from > to ? 'The range starts after it ends.' : undefined;
  const ready = rangeError === undefined && from !== '' && to !== '';

  const run = async () => {
    if (!ready || running || oldest === 'reading') return;
    setRunning(true);
    setResult(null);
    try {
      const payload: Record<string, unknown> = { from, to };
      if (brandId) payload['brandId'] = brandId;
      const outcome = await api.post<ExportOutcome>('/api/players/export', payload);
      setResult(outcome);
      toast(
        `${outcome.ref} · ${formatCount(outcome.rowCount)} player${outcome.rowCount === 1 ? '' : 's'} exported` +
          (outcome.truncated ? ` · stopped at the ${formatCount(outcome.rowCap)}-row cap` : ''),
      );
      await downloadRun(outcome.ref, showError, toast);
    } catch (error: unknown) {
      showError({
        title: 'Could not export the roster',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The export did not run. Nothing was produced and nothing was recorded as produced.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Export the roster"
      wide
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            disabled={!ready || running || oldest === 'reading'}
            aria-describedby="players-export-gap"
            onClick={() => void run()}
          >
            {running ? 'Running…' : 'Run the export'}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: '13px' }}>
        The file is produced by the reports engine with read-only access, stored as a
        report artefact and downloaded from it — so the same run can be downloaded again next week
        byte for byte, and the export itself is in the audit trail.
      </p>

      <div className="stack">
        <Field
          label="Registered from"
          error={rangeError}
          hint={
            oldest === 'reading'
              ? `Reading the oldest registration in ${brandLabel}. The export waits until it is known, so nobody who joined earlier is left out.`
              : oldest === 'read' && earliest
                ? `Defaulted to ${earliest}, the oldest registration in ${brandLabel} — so the whole roster is covered unless you narrow it.`
                : oldest === 'none'
                  ? `Nobody is registered in ${brandLabel} yet, so this starts three years ago.`
                  : 'The oldest registration could not be read, so this fell back to three years ago. Widen it if the brand has older accounts.'
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="date"
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Registered to"
          hint="Inclusive of the whole of that day. Both dates are Manila gaming days, midnight to midnight Manila time."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="date"
              aria-describedby={describedBy}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Format"
          hint="CSV, quoted, with a leading apostrophe on any cell starting = + - or @ so a spreadsheet cannot execute it on open."
        >
          {({ id, describedBy }) => (
            <Select id={id} aria-describedby={describedBy} value="CSV" disabled>
              <option value="CSV">CSV</option>
            </Select>
          )}
        </Field>

        {result && (
          <div className="notice notice-quiet">
            <Icon name="check-circle" />
            <div>
              <strong>{result.ref}</strong> produced {formatCount(result.rowCount)} row
              {result.rowCount === 1 ? '' : 's'} in {formatCount(result.durationMs)}ms
              {result.truncated ? (
                <>
                  {' '}
                  and <strong>stopped at the {formatCount(result.rowCap)}-row cap</strong>, so this
                  file is not the whole roster — narrow the window and run it again.
                </>
              ) : (
                '.'
              )}{' '}
              The download has started. The same file is on <strong>Reports</strong> under run
              history and can be downloaded again from there.
            </div>
          </div>
        )}

        <div className="notice notice-quiet" id="players-export-gap">
          <Icon name="warning-circle" />
          <div>
            <strong>The file carries the player REFERENCE and no name, email or phone.</strong> That
            is a rule of the reports model rather than a choice made here: those are not dimensions
            of the roster source, and the engine refuses a column that is not in its map. An export
            is mailed around and opened on somebody&rsquo;s laptop; it identifies an account, not a
            person.
            <br />
            <strong>Your brand scope is applied from the session</strong>, never from this form, so
            an export cannot reach a brand you cannot open. It covers <strong>{brandLabel}</strong>.
            {filtersApplied.length > 0 ? (
              <>
                {' '}
                <strong>
                  The roster&rsquo;s own filters ({filtersApplied.join(', ')}) are NOT applied.
                </strong>{' '}
                The engine filters on a date range and a brand and nothing else — any other filter
                would have to be built from whatever a request sent, which is exactly what the
                engine exists to prevent. Status, KYC, tier, agent and residence are <em>columns</em> in
                the file, so narrow it in the spreadsheet.
              </>
            ) : (
              <>
                {' '}
                Status, KYC, tier, agent, risk and residence are columns in the file rather than
                filters on it — the engine takes a date range and a brand and nothing else. The file
                also carries what the brand&rsquo;s residency policy would decide about each
                player&rsquo;s country today, which is the column a residency question is actually
                asked with.
              </>
            )}
            <br />
            This runs under <span className="tabular">audit.export</span>, the same capability
            that runs and downloads every other report — which is why the control is absent
            altogether for a role that does not hold it rather than shown and refused.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
